import { z } from "zod";

import { password } from "./password.ts";

export const UserRole = z.enum(["admin", "member"]);
export type UserRole = z.infer<typeof UserRole>;

/**
 * What a user may use. "containers": projects and their containers; "studio": Agent Studio; "chat": the Chat
 * section; "accounts": connecting their own provider accounts; "sharedAccounts": using the accounts shared on the
 * platform; "admin": the platform's settings (users, updates, tools, sign-in, public apps), the admin role.
 */
export const FEATURES = ["containers", "studio", "chat", "accounts", "sharedAccounts", "admin"] as const;
export const Feature = z.enum(FEATURES);
export type Feature = z.infer<typeof Feature>;
/** Starting points in the users page; any mix can be ticked afterwards. */
export const FEATURE_TEMPLATES: Record<"admin" | "allButSettings" | "chat" | "containersStudio", Feature[]> = {
  admin: [...FEATURES],
  allButSettings: FEATURES.filter((f) => f !== "admin"),
  chat: ["chat", "sharedAccounts"],
  containersStudio: ["containers", "studio", "sharedAccounts"],
};

export const PublicUser = z.object({
  id: z.string(),
  email: z.email(),
  name: z.string().min(1).max(80),
  /** the user's photo (a small data: image), or null for their initial */
  avatar: z.string().nullable(),
  role: UserRole,
  /** the platform's owner (the first account): every feature, and nobody else sees or changes it */
  owner: z.boolean(),
  features: z.array(Feature),
  /** made by an admin with a password they chose: it is changed on the first sign-in */
  mustChangePassword: z.boolean(),
  emailVerifiedAt: z.string().nullable(),
  totpEnabled: z.boolean(),
  createdAt: z.string(),
});
export type PublicUser = z.infer<typeof PublicUser>;

/** A photo for a person: a small square image, cropped in the browser (like an agent's). */
export const AvatarImage = z.string().regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/).max(160_000);

/** What a user changes about themselves: their name and photo (null removes it). */
export const UpdateProfileInput = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  avatar: AvatarImage.nullable().optional(),
});
export type UpdateProfileInput = z.infer<typeof UpdateProfileInput>;

export const CreateUserInput = z.object({
  name: z.string().trim().min(1).max(80),
  email: z.email().transform((e) => e.toLowerCase()),
  password,
  features: z.array(Feature).max(FEATURES.length),
});
export type CreateUserInput = z.infer<typeof CreateUserInput>;
export const SetUserFeaturesInput = z.object({ features: z.array(Feature).max(FEATURES.length) });
export type SetUserFeaturesInput = z.infer<typeof SetUserFeaturesInput>;
export const ResetUserPasswordInput = z.object({ password });
export type ResetUserPasswordInput = z.infer<typeof ResetUserPasswordInput>;

export const SessionInfo = z.object({
  id: z.string(),
  current: z.boolean(),
  userAgent: z.string().nullable(),
  ip: z.string().nullable(),
  createdAt: z.string(),
  lastSeenAt: z.string(),
});
export type SessionInfo = z.infer<typeof SessionInfo>;

export const Invite = z.object({
  id: z.string(),
  code: z.string(),
  email: z.email().nullable(),
  role: UserRole,
  createdBy: z.string(),
  createdAt: z.string(),
  expiresAt: z.string(),
  usedAt: z.string().nullable(),
  usedBy: z.string().nullable(),
});
export type Invite = z.infer<typeof Invite>;

export const CreateInviteInput = z.object({
  email: z.email().optional(),
  role: UserRole.default("member"),
  expiresInHours: z.number().int().min(1).max(24 * 30).default(72),
});
export type CreateInviteInput = z.infer<typeof CreateInviteInput>;

// ---------------- web push ----------------

/** Hours a device stays quiet: only failures and approval requests come through (times in its own time zone). */
export const QuietHours = z.object({ from: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/), to: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/), tz: z.string().min(1).max(64) });
export type QuietHours = z.infer<typeof QuietHours>;
export const PushPrefs = z.object({ runs: z.boolean(), approvals: z.boolean(), schedules: z.boolean(), quiet: QuietHours.nullable().optional() });

/** Whether `now` falls in the quiet hours (which may run past midnight, 22:00 → 07:00). */
export function inQuietHours(q: QuietHours | null | undefined, now = new Date()): boolean {
  if (!q || q.from === q.to) return false;
  let hm: string;
  try {
    hm = new Intl.DateTimeFormat("en-GB", { timeZone: q.tz, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(now);
  } catch {
    return false; // an unknown time zone: never silence
  }
  return q.from < q.to ? hm >= q.from && hm < q.to : hm >= q.from || hm < q.to;
}
export type PushPrefs = z.infer<typeof PushPrefs>;

export const PushSubscriptionInput = z.object({
  endpoint: z.string().url().max(2000),
  keys: z.object({ p256dh: z.string().min(1).max(500), auth: z.string().min(1).max(200) }),
  label: z.string().trim().max(80).default(""),
  /** The subscription this one replaces (the browser rotated it): its name and notification choices carry over. */
  replaces: z.string().url().max(2000).optional(),
});
export type PushSubscriptionInput = z.infer<typeof PushSubscriptionInput>;

export const PushSubscriptionInfo = z.object({
  id: z.string(),
  endpoint: z.string(),
  label: z.string(),
  prefs: PushPrefs,
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type PushSubscriptionInfo = z.infer<typeof PushSubscriptionInfo>;

export const UpdatePushSubscriptionInput = z.object({ label: z.string().trim().max(80).optional(), prefs: PushPrefs.partial().optional() });

export const GoogleStatus = z.object({ configured: z.boolean(), connected: z.boolean(), email: z.string().nullable(), driveScope: z.boolean() });
export type GoogleStatus = z.infer<typeof GoogleStatus>;
export const DriveExportResult = z.object({ fileId: z.string(), name: z.string(), webViewLink: z.string().nullable(), bytes: z.number() });
export type DriveExportResult = z.infer<typeof DriveExportResult>;

/** An outside app (MCP host) the user authorized. */
export const OauthGrant = z.object({ id: z.string(), clientId: z.string(), clientName: z.string(), clientUri: z.string().nullable(), scope: z.string(), createdAt: z.string(), lastUsedAt: z.string().nullable() });
export type OauthGrant = z.infer<typeof OauthGrant>;
export const OauthConsentInfo = z.object({ clientId: z.string(), clientName: z.string(), clientUri: z.string().nullable(), logoUri: z.string().nullable(), redirectUri: z.string(), scope: z.string(), scopes: z.array(z.string()), scopeIds: z.array(z.string()).default([]) });
export type OauthConsentInfo = z.infer<typeof OauthConsentInfo>;
export const OauthDecisionInput = z.object({
  client_id: z.string().min(1),
  redirect_uri: z.string().url(),
  scope: z.string().default(""),
  state: z.string().max(512).default(""),
  code_challenge: z.string().min(43).max(128),
  code_challenge_method: z.literal("S256"),
  resource: z.string().optional(),
  approve: z.boolean(),
});
